<template>
  <div class="page-voiceover">
    <SectionHeader title="当前页配音" />

    <!-- Voice card -->
    <div
      class="page-voiceover__card"
      :class="{ 'page-voiceover__card--disabled': disabled }"
      @click="!disabled && openDialog()"
    >
      <div class="page-voiceover__avatar">
        <img
          v-if="portrait"
          :src="portrait"
          class="page-voiceover__portrait-img"
        />
        <SvgIcon v-else name="editor-music" :size="20" color="#6033E1" />
      </div>
      <span class="page-voiceover__name">{{ voiceDisplayName }}</span>
      <SvgIcon name="editor-chevron-right" :size="15" color="#94A3B8" class="page-voiceover__arrow" />
    </div>

    <div class="page-voiceover__divider"></div>

    <!-- Play audio button -->
    <button
      class="page-voiceover__play-btn"
      :class="{ 'page-voiceover__play-btn--playing': isPlaying }"
      :disabled="!hasTts || disabled"
      @click="handlePlayAudio"
    >
      <SvgIcon :name="isPlaying ? 'book-pause' : 'play'" :size="11" :color="isPlaying ? '#6033E1' : '#64748B'" />
      <span>{{ isPlaying ? '停止播放' : '播放音频' }}</span>
    </button>

    <VoiceSelectDialog
      v-model="dialogVisible"
      @confirm="handleVoiceConfirm"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import SvgIcon from '@/components/common/icon/SvgIcon.vue';
import SectionHeader from './SectionHeader.vue';
import VoiceSelectDialog from '@/views/manage/create/VoiceSelectDialog.vue';
import type { VoiceSelectResult } from '@/views/manage/create/VoiceSelectDialog.vue';

const props = defineProps<{
  /** TTS 音频 URL */
  audioUrl?: string | null;
  /** 配音角色名称 */
  voiceName?: string | null;
  /** 是否有配音数据 */
  hasTts?: boolean;
  /** 是否正在播放 */
  isPlaying?: boolean;
  /** 配音角色头像 URL */
  portrait?: string | null;
  /** 配音角色描述 */
  description?: string | null;
  /** 是否禁用（无文本元素时禁用） */
  disabled?: boolean;
}>();

const emit = defineEmits<{
  'confirm': [result: VoiceSelectResult];
  'play-audio': [];
}>();

const dialogVisible = ref(false);

const voiceDisplayName = computed(() => {
  if (!props.hasTts) return '未配置';
  const name = props.voiceName || '配音';
  const desc = props.description;
  return desc ? `${name} | ${desc}` : name;
});

function openDialog() {
  dialogVisible.value = true;
}

function handlePlayAudio() {
  emit('play-audio');
}

function handleVoiceConfirm(result: VoiceSelectResult) {
  emit('confirm', result);
  dialogVisible.value = false;
}

defineExpose({ openDialog });
</script>

<style scoped>
.page-voiceover {
  width: 100%;
  box-sizing: border-box;
  padding: 20px 20px 16px;
}

/* Voice card */
.page-voiceover__card {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 15px;
  padding: 12px 14px;
  height: 68px;
  background-color: #fbf9ff;
  border-radius: 12px;
  cursor: pointer;
  box-sizing: border-box;
  transition: background-color 0.2s;
}

.page-voiceover__card:hover {
  background-color: #f3f0ff;
}

.page-voiceover__card--disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-voiceover__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: #ede8ff;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
}

.page-voiceover__portrait-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.page-voiceover__name {
  flex: 1;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #333333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.page-voiceover__arrow {
  flex-shrink: 0;
}

/* Divider */
.page-voiceover__divider {
  height: 1px;
  background-color: #eee;
  margin-top: 16px;
}

/* Play button */
.page-voiceover__play-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 34px;
  margin-top: 15px;
  border: 1px solid #eff0f7;
  border-radius: 10px;
  background-color: #fff;
  cursor: pointer;
  transition:
    background-color 0.2s,
    border-color 0.2s;
}

.page-voiceover__play-btn:hover:not(:disabled) {
  background-color: #f8fafc;
  border-color: #e2e8f0;
}

.page-voiceover__play-btn:active:not(:disabled) {
  opacity: 0.8;
}

.page-voiceover__play-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-voiceover__play-btn--playing {
  border-color: #6033e1;
  background-color: #fbf9ff;
}

.page-voiceover__play-btn--playing:hover:not(:disabled) {
  background-color: #f3f0ff;
  border-color: #6033e1;
}

.page-voiceover__play-btn span {
  font-weight: 400;
  font-size: 13px;
  line-height: 19.5px;
  color: #64748b;
}

.page-voiceover__play-btn--playing span {
  color: #6033e1;
}
</style>
